import { useEffect } from "react";
import { MapContainer, TileLayer, useMap, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default Leaflet icon paths (only relevant if we used default markers; we draw our own).
delete L.Icon.Default.prototype._getIconUrl;

// Recenter helper — smoothly pans when coords change
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], map.getZoom() || 14, { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

/**
 * RadarMap
 * Full-bleed dark Leaflet tile layer that lives BENEATH the radar overlay.
 * Dots/rings/sweep are painted by the parent Radar page on top.
 * Optionally renders live hangout member markers when the user is in an active hangout.
 */
export default function RadarMap({ lat, lng, hangoutMembers = [], selfId }) {
  const hasCoords = lat != null && lng != null;
  const center = hasCoords ? [lat, lng] : [40.7128, -74.006]; // fallback: NYC

  return (
    <div className="absolute inset-0" data-testid="radar-map-canvas">
      <MapContainer
        center={center}
        zoom={14}
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        style={{ height: "100%", width: "100%", background: "#05050A" }}
      >
        {/* CartoDB Dark Matter — no API key, matches the neon theme */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={19}
        />
        {hasCoords && <Recenter lat={lat} lng={lng} />}
        {hangoutMembers
          .filter((m) => m.id !== selfId && m.lat != null && m.lng != null)
          .map((m) => (
            <CircleMarker
              key={m.id}
              center={[m.lat, m.lng]}
              radius={8}
              pathOptions={{
                color: "#FF007F",
                fillColor: "#FF007F",
                fillOpacity: 0.85,
                weight: 2,
              }}
            >
              <Tooltip direction="top" offset={[0, -6]} opacity={1} permanent>
                <span style={{ fontFamily: "monospace", fontSize: 10 }}>{m.display_name}</span>
              </Tooltip>
            </CircleMarker>
          ))}
      </MapContainer>
      {/* Cyan tint to keep the theme cohesive */}
      <div className="pointer-events-none absolute inset-0 bg-[#00E5FF]/[0.04] mix-blend-overlay" />
      {/* Vignette so the radar rings pop */}
      <div className="pointer-events-none absolute inset-0" style={{
        background: "radial-gradient(circle at center, transparent 40%, rgba(5,5,10,0.85) 90%)",
      }} />
    </div>
  );
}
